import type { RuntimeConfig } from "./config";
import type { LmdUpsertLeadRequest } from "./models/lmd";

export const LEAD_INTAKE_APIM_SUBSCRIPTION_HEADER = "Ocp-Apim-Subscription-Key";

export interface LeadIntakeSubmissionResult {
  status: string;
  error: string;
  ldLeadId: string;
  ldGuid: string;
}

export function leadIntakeApimBaseUrl(config: RuntimeConfig): string {
  return config.leadIntakeApiBaseUrl;
}

function escapeXml(value: unknown): string {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");
}

function tag(name: string, value: unknown): string {
  return `  <${name}>${escapeXml(value)}</${name}>`;
}

function randomId(): string {
  return `tv-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function phoneDigits(value: unknown): string {
  return String(value ?? "").replace(/\D/g, "");
}

function numberText(value: unknown): string {
  return typeof value === "number" && Number.isFinite(value) ? String(value) : "";
}

function militaryText(value: unknown): string {
  if (value === true) return "T";
  if (value === false) return "F";
  return "";
}

function militaryBranchText(value: unknown): string {
  switch (value) {
    case "Army":
      return "1";
    case "Navy":
      return "2";
    case "AirForce":
      return "3";
    case "MarineCorps":
      return "4";
    case "SpaceForce":
      return "5";
    case "CoastGuard":
      return "6";
    case "ArmyNationalGuard":
      return "7";
    case "AirNationalGuard":
      return "8";
    default:
      return "";
  }
}

function propertyTypeText(value: unknown): string {
  switch (value) {
    case "SingleFamily":
    case "MultiFamily":
    case "Condo":
    case "Townhouse":
    case "Coop":
    case "Mobile":
    case "Manufactured":
      return value;
    default:
      return "SingleFamily";
  }
}

export function buildLeadIntakeXmlPayload(
  request: LmdUpsertLeadRequest,
  correlationId: string,
  password: string,
): string {
  const lead = request.lead;
  const borrower = lead.borrower;
  const application = lead.application;
  const lines = [
    "<loandepotlead>",
    tag("LeadSource", lead.leadSource),
    tag("LDGUID", "true"),
    tag("Password", password),
    tag("LeadId", randomId()),
    tag("DateCreated", new Date().toISOString()),
    tag("FirstName", borrower.firstName),
    tag("LastName", borrower.lastName),
    tag("Email", borrower.emailAddress),
    tag("StreetAddress", application.propertyStreetAddress),
    tag("City", application.propertyCity),
    tag("State", application.propertyState),
    tag("ZipCode", application.propertyZipCode),
    tag("Phone1", phoneDigits(borrower.phone1Number)),
    tag("CreditProfile", application.creditProfile),
    tag("FirstMortgageBalance", numberText(application.currentLoanBalance)),
    tag("LoanType", application.loanPurpose),
    tag("PropertyType", propertyTypeText(application.propertyType)),
    tag("DesiredLoanAmount", numberText(application.loanAmount)),
    tag("DownPayment", numberText(application.downPayment)),
    tag("Occupancy", "Owner Occupied"),
    tag("ExistingPropertyValue", numberText(application.estimatedPropertyValue)),
    tag("MailCity", borrower.mailCity),
    tag("Street", borrower.mailStreetAddress),
    tag("MailState", borrower.mailState),
    tag("PostalCode", borrower.mailZipCode),
    tag("IsMilitary", militaryText(application.isMilitary)),
    tag("MilitaryBranch", militaryBranchText(application.militaryBranch)),
    tag("Status", lead.supplemental.status),
    tag("MarketingAttribute1", "TurboVets"),
    tag("MarketingAttribute2", correlationId),
    "</loandepotlead>",
  ];
  return lines.join("\n");
}

function extractXmlTag(xml: string, name: string): string {
  const match = xml.match(new RegExp(`<${name}>([^<]*)</${name}>`, "i"));
  return match?.[1]?.trim() ?? "";
}

export function parseLeadIntakeResponse(xml: string): LeadIntakeSubmissionResult {
  return {
    status: extractXmlTag(xml, "status"),
    error: extractXmlTag(xml, "error"),
    ldLeadId: extractXmlTag(xml, "LDLeadId"),
    ldGuid: extractXmlTag(xml, "LDGUID"),
  };
}
